/**
 * @file Isolated textarea for the agent prompt. Owns its value state so
 * keystrokes don't propagate into `ModelInput` or its sibling buttons.
 * Handles Enter submission internally and exposes `submit()` via ref so
 * the external send button can trigger the same code path.
 */
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Input, theme } from 'antd';

export interface PromptComposerHandle {
    submit: () => void;
}

interface PromptComposerProps {
    onSubmit: (prompt: string) => void;
    disabled: boolean;
}

const PromptComposer = forwardRef<PromptComposerHandle, PromptComposerProps>(
    ({ onSubmit, disabled }, ref) => {
        const [value, setValue] = useState('');
        const { token } = theme.useToken();

        const submit = () => {
            if (value.trim() === '') return;
            const prompt = value;
            setValue('');
            onSubmit(prompt);
        };

        useImperativeHandle(ref, () => ({ submit }));

        return (
            <Input.TextArea
                id="inputPrompt"
                aria-label="model-prompt-input"
                placeholder="How can system assist you?"
                allowClear
                maxLength={8000}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (!disabled) submit();
                    }
                }}
                autoSize={{ minRows: 1, maxRows: 10 }}
                style={{
                    borderColor: token.colorPrimaryBorder,
                    boxShadow: token.boxShadow,
                }}
            />
        );
    },
);

export default PromptComposer;
